import {
  ArrowDropDown,
  ArrowOutward,
  CircleOutlined,
  Close,
  DrawOutlined,
  SquareOutlined,
  SquareRounded,
  Undo,
} from '@mui/icons-material';
import LayersIcon from '@mui/icons-material/Layers';
import { Box, IconButton, Slider, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { SketchPicker } from 'react-color';
import { useCanvasSketchContext } from '../../../../../contexts/CanvasSketchContext';
import { useSessionContext } from '../../../../../contexts/SessionContext';
import { IImage } from '../../../../../global/interfaces';
import { useEditorStyles } from '../../styles';
import theme from '../../../../../theme';
import { COLORS } from '../../../../../utils/colors';

interface ImageEditorToolbarProps {
  setSelectedImage: React.Dispatch<React.SetStateAction<IImage | null>>;
}

const ImageEditorToolbar: React.FC<ImageEditorToolbarProps> = ({
  setSelectedImage,
}) => {
  const styles = useEditorStyles();
  const handleClose = () => {
    setSelectedImage(null);
  };

  const {
    color,
    activeShape,
    activeTab,
    penSize,
    setPenSize,
    isPenVisible,
    isLayersVisible,
    strokeList,
    isColorPaletteOpen,
    croppedUndoImages,
    paletteRef,
    shapesRef,
    isShapesVisible,
    setCroppedUndoImages,
    setIsPenVisible,
    setIsLayersVisible,
    handleSlider,
    handleSetActiveShape,
    setActiveTab,
    handleClickOutside,
    handleToggleShapesModal,
    handleToggleColorPalette,
    handleSelectColor,
    undoChanges,
  } = useCanvasSketchContext();

  const { strokesLayer, setStrokesLayer, selectedImage } = useSessionContext();

  useEffect(() => {
    setPenSize(penSize);
  }, [penSize]);

  const CANVAS_SHAPES = [
    { label: 'SQUARE', Icon: SquareOutlined },
    { label: 'CIRCLE', Icon: CircleOutlined },
    { label: 'ARROW', Icon: ArrowOutward },
  ];

  const IMAGE_EDITOR_ICONS = [
    {
      Icon: Close,
      label: 'Close',
      handleClick: () => {
        handleClose();
      },
    },
    // {
    //   Icon: Crop,
    //   label: 'Crop',
    //   handleClick: (event: any) => {
    //     event.stopPropagation();
    //     handleToggleCrop();
    //   },
    // },
    {
      Icon: Undo,
      label: 'Undo',
      handleClick: () => undoChanges(),
    },
    {
      Icon:
        activeShape === 'SQUARE'
          ? SquareOutlined
          : activeShape === 'CIRCLE'
            ? CircleOutlined
            : activeShape === 'ARROW'
              ? ArrowOutward
              : SquareOutlined,
      label: 'Shape',
      handleClick: (event: any) => {
        event.stopPropagation();
        handleClickOutside();
        handleToggleShapesModal();
      },
    },
    {
      Icon: DrawOutlined,
      label: 'Draw',
      handleClick: (event: any) => {
        event.stopPropagation();
        handleClickOutside();
        setIsPenVisible(!isPenVisible);
        setActiveTab('DRAW');
      },
    },
    {
      Icon: SquareRounded,
      label: 'Colour',
      handleClick: (event: any) => {
        event.stopPropagation();
        handleClickOutside();
        handleToggleColorPalette(!isColorPaletteOpen);
      },
    },
    /*{
      Icon: LayersIcon,
      label: 'Layers',
      handleClick: (event: any) => {
        event.stopPropagation();
        handleClickOutside();
        setIsLayersVisible(!isLayersVisible);
      },
    },*/
  ];

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <>
      <Box display={'flex'}>
        {IMAGE_EDITOR_ICONS.map(({ Icon, label, handleClick }, index) => (
          <Box
            bgcolor={activeTab === label ? COLORS.LIGHT_GRAY : '#fff'}
            key={index}
            sx={{
              ...styles.canvasIcon,
              display: 'flex',
            }}
            color="black"
            position="relative">
            <IconButton
              disableRipple
              onClick={handleClick}
              color="inherit"
              sx={{ paddingBottom: '0.1rem' }}
              disabled={label === 'Undo' && !strokeList?.length}>
              <Icon
                sx={{
                  fontSize: '1.305rem',
                  color: label === 'Colour' ? color : 'inherit',
                }}
              />
            </IconButton>
            <Box display="flex" alignItems="flex-end">
              <Typography fontSize=".55rem" textAlign="center" fontWeight={500}>
                {label}
              </Typography>
              {(label === 'Shape' || label === 'Layers') && (
                <ArrowDropDown style={{ fontSize: '.875rem' }} />
              )}
            </Box>
          </Box>
        ))}
      </Box>
      {isShapesVisible && (
        <Box
          ref={shapesRef}
          sx={styles.canvasShapesDocument}
          border={`1px solid ${theme.palette.divider}`}>
          {CANVAS_SHAPES.map(({ label, Icon }) => {
            return (
              <Box
                key={label}
                display="flex"
                width="100%"
                bgcolor={
                  activeShape === label ? COLORS.LIGHT_GRAY : COLORS.WHITE
                }>
                <IconButton
                  disableRipple
                  onClick={() => handleSetActiveShape(label)}>
                  <Icon sx={{ color: 'black' }} />
                  <Typography ml=".4375rem" fontSize=".75rem">
                    {label}
                  </Typography>
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}

      {isPenVisible && (
        <Box
          ref={shapesRef}
          sx={styles.canvasPenDocument}
          onClick={(event: any) => {
            event.stopPropagation();
          }}
          border={`1px solid ${theme.palette.divider}`}>
          <Slider
            aria-label="Volume"
            min={1}
            max={20}
            value={penSize}
            onChange={handleSlider}
          />
        </Box>
      )}

      {isColorPaletteOpen && (
        <Box
          ref={paletteRef}
          position="absolute"
          left="7rem"
          top="3.5rem"
          onClick={(event: any) => {
            event.stopPropagation();
          }}
          zIndex={10}>
          <SketchPicker color={color} onChangeComplete={handleSelectColor} />
        </Box>
      )}

      {isLayersVisible && (
        <Box
          ref={shapesRef}
          sx={styles.canvaslayersDocument}
          onClick={(event: any) => {
            event.stopPropagation();
          }}
          border={`1px solid ${theme.palette.divider}`}>
          {selectedImage?.strokes.map((layerStrokes, index) => {
            //draw strokesLayer on a small canvas
            return (
              <Box
                sx={styles.strokesLayer}
                key={'label'}
                onClick={() => setStrokesLayer(index)}
                bgcolor={
                  index === strokesLayer ? COLORS.LIGHT_GRAY : COLORS.WHITE
                }>
                {!index ? 'Blank' : `Layer ${index}`}
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
};

export default ImageEditorToolbar;
